import {
  addUtcDays,
  addUtcMonths,
  formatUtcCalendarDate,
  formatUTCDate,
  getCalendarGridDays,
  getMonthBounds,
  isSameUtcDay,
  isSameUtcMonth,
  printDateMonthAndYear
} from '@/helpers/dateHelper';
import { strings } from '@/services/translation/strings';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';

interface DayPickerProps {
  value: Date;
  onChange: (newValue: Date) => void;
  position?: 'left' | 'center';
  showLabels?: boolean;
  minDate?: Date;
}

const WEEKDAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const defaultHistoryLimit = new Date(Date.UTC(2025, 0, 1));

export const DayPicker: React.FC<DayPickerProps> = ({
  value,
  onChange,
  position = 'center',
  showLabels = true,
  minDate
}) => {
  const limit = minDate ?? defaultHistoryLimit;
  const historyLimitMs = formatUTCDate(limit).getTime();
  const limitMonthStartMs = getMonthBounds(limit)[0].getTime();

  const valueYear = value.getUTCFullYear();
  const valueMonth = value.getUTCMonth();

  const [viewDate, setViewDate] = React.useState<Date>(() => addUtcMonths(value, 0));

  useEffect(() => {
    setViewDate(new Date(Date.UTC(valueYear, valueMonth, 1)));
  }, [valueYear, valueMonth]);

  const canGoBack = addUtcDays(value, -1).getTime() >= historyLimitMs;
  const canGoBackMonth = addUtcMonths(viewDate, -1).getTime() >= limitMonthStartMs;

  const calendarDays = useMemo(() => getCalendarGridDays(viewDate), [viewDate]);

  const dateDisplay = formatUtcCalendarDate(value, 'do MMMM yyyy');
  const viewMonthLabel = printDateMonthAndYear(viewDate);

  return (
    <div className="flex justify-between items-stretch gap-5">
      <button
        type="button"
        aria-label="Previous Day"
        className={twMerge(
          'px-3 py-2 rounded-lg border bg-white flex items-center gap-1',
          canGoBack
            ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        )}
        onClick={() => onChange(addUtcDays(value, -1))}
        disabled={!canGoBack}
      >
        <FiChevronLeft className="w-6 h-6" />
        {showLabels && <p className="hidden sm:block">{strings.common.previous}</p>}
      </button>

      <Popover className="relative w-60">
        <PopoverButton className="h-full w-full py-3 border border-border rounded-md bg-white flex justify-center items-center leading-none px-3 gap-3">
          <FiCalendar />
          {dateDisplay}
        </PopoverButton>

        <PopoverPanel
          className="absolute border border-border bg-white p-5 z-50 shadow w-[320px]"
          style={{ left: position === 'center' ? 'calc(50% - 160px)' : undefined }}
        >
          {({ close }) => (
            <>
              <div className="flex gap-2 justify-center items-center mb-4">
                <button
                  type="button"
                  onClick={() => setViewDate(addUtcMonths(viewDate, -1))}
                  disabled={!canGoBackMonth}
                  aria-label="Previous month"
                >
                  <FiChevronLeft className="w-5 h-5" style={{ color: !canGoBackMonth ? 'grey' : undefined }} />
                </button>
                <div className="py-1 px-6 bg-active rounded-md border border-border font-bold text-sm">
                  {viewMonthLabel}
                </div>
                <button type="button" onClick={() => setViewDate(addUtcMonths(viewDate, 1))} aria-label="Next month">
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {WEEKDAY_HEADERS.map(h => (
                  <div key={h} className="text-center text-xs font-semibold text-gray-400 py-1">
                    {h}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {calendarDays.map(day => {
                  const isSelected = isSameUtcDay(day, value);
                  const isDisabled = day.getTime() < historyLimitMs;
                  const inMonth = isSameUtcMonth(day, viewDate);

                  return (
                    <div key={day.toISOString()} className="flex items-center justify-center p-0.5">
                      <button
                        type="button"
                        onClick={() => {
                          onChange(day);
                          close();
                        }}
                        disabled={isDisabled}
                        className={[
                          'w-8 h-8 rounded-full text-sm flex items-center justify-center transition-colors',
                          isSelected
                            ? 'bg-blue-600 text-white font-bold'
                            : isDisabled
                              ? 'text-gray-300 cursor-default'
                              : inMonth
                                ? 'hover:bg-gray-100 cursor-pointer'
                                : 'text-gray-400 hover:bg-gray-50 cursor-pointer'
                        ].join(' ')}
                      >
                        {day.getUTCDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </PopoverPanel>
      </Popover>

      <button
        type="button"
        aria-label="Next Day"
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg border bg-white flex items-center gap-1"
        onClick={() => onChange(addUtcDays(value, 1))}
      >
        {showLabels && <p className="hidden sm:block">{strings.common.next}</p>}
        <FiChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};
